import { plain } from "./names";
import { bp } from "./slug";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Info, CircleAlert, Loader2, Lock, WifiOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "./api";

const TONES: Record<string, string> = {
  RETAIN: "green", APPLIED: "green", DELIVERED: "green", ADEQUATE_FOR_SCOPE: "green", COVERED: "green", COMPLETE: "green", FUNDED: "green", AVAILABLE: "green",
  WITHIN_ENVELOPE: "green", EXECUTABLE_FOR_SCOPE: "green", DISPATCHABLE: "green", ADMITTED: "green", SUPPORTED: "green", ACCEPTED: "green", PERMITTED: "green",
  CANDIDATE: "blue", PROCESSING: "blue", PENDING: "blue", QUEUED: "blue", SENDING: "blue", READY_FOR_OWNER: "blue", REVIEWED: "blue", SEED_BOUNDED_LEARNING: "blue",
  REVALIDATE: "amber", UNKNOWN: "amber", GAP: "amber", EVIDENCE_OPEN: "amber", DRAFT: "amber", PROPOSED: "amber", OPEN: "amber", INCOMPLETE: "amber",
  UNCONFIRMED: "amber", EVALUATION_ONLY: "amber", HELD: "amber", PENDING_OWNER_REVIEW: "amber", REVIEW_REQUIRED: "amber", MISSING_ANNEX: "amber", NOT_ASSESSED: "amber",
  PREPARATION_ONLY: "amber", UNRESOLVED: "amber", NOT_EXECUTABLE: "red", CONFLICT: "red", FUNDING_GAP: "red", BLOCKED: "red", FAILED: "red", UNCERTAIN: "orange",
  QUARANTINED: "red", REJECTED: "red", NOT_ADMITTED: "red", DENIED: "red", UNSUPPORTED: "red", NEW: "blue", CORRECTED: "orange", RECONCILING: "orange",
};
export const tone = (s?: string) => TONES[(s || "").toUpperCase()] || "";
export const label = (s?: string) => { const t = (s || "-").replace(/_/g, " "); return /^[A-Z0-9 ]+$/.test(t) ? t.charAt(0) + t.slice(1).toLowerCase() : t; };

export const Pill = ({ s, children, testid }: { s?: string; children?: React.ReactNode; testid?: string }) => (
  <span className={`pill ${tone(s)}`} data-testid={testid} title={s ? `${label(s)}${tone(s) === "amber" || tone(s) === "orange" ? ": attention" : tone(s) === "red" ? ": adverse / blocked" : tone(s) === "green" ? ": established" : ""}` : undefined}>{children ?? label(s)}</span>
);

export const Note = ({ children, kind = "neutral", testid }: { children: React.ReactNode; kind?: "neutral" | "red" | "warn"; testid?: string }) => (
  <div className={`notice ${kind === "warn" ? "" : kind}`} role={kind === "red" ? "alert" : "note"} data-testid={testid}>
    {kind === "red" ? <CircleAlert size={15} /> : <Info size={15} />}<div>{children}</div>
  </div>
);

export const Surface = ({ title, extra, children, testid }: { title: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; testid?: string }) => (
  <section className="surface" data-testid={testid}>
    <div className="surface-head spread"><h2>{title}</h2>{extra}</div>
    {children}
  </section>
);

const NEXT: Record<string, [string, string]> = {
  "funding-table": ["Add a funding source from a reviewed clause (Source review → map as funding)", "review"], "obligation-table": ["Add a dated obligation from a reviewed clause (Source review → map as obligation)", "review"],
  "requirement-table": ["Attach evidence or define missing-evidence work from the requirement (Source review → map as requirement)", "review"], "dependency-table": ["Add a requirement from source", "review"],
  "condition-table": ["Map an accepted meaning as a condition", "review"], "appointment-table": ["Record an authorised decision once its owner and terms exist (Resolve → Exact-scope decision)", "approval"],
  "participant-table": ["Add a participant commitment", "commitments"], "notice-table": ["Prepare a notice draft (never dispatched)", "notices"], "remaining-table": ["Prepare the recipient packet (Deliver → Readiness Pack)", "packet"], "source-table": ["Upload a source document (Configure → Source library)", "sources"], "correction-table": ["Record a successor revision when a source or finding changes", "correction"], "cost-table": ["Add a priced task or cost line from a reviewed quote", "review"],
};
function EmptyNext({ testid }: { testid?: string }) {
  const loc = typeof window !== "undefined" ? window.location.pathname.split("/") : [];
  const pid = loc[1] === "p" ? loc[2] : null;
  const hit = (testid && NEXT[testid]) || ["Upload a source or map an accepted meaning to create the first typed record", "sources"];
  if (!pid) return null;
  return <p className="tiny" style={{ margin: "6px 0 0" }}><a href={`${bp(pid)}/${hit[1]}`} data-testid={`${testid}-next`}>{hit[0]} →</a></p>;
}

export function Table({ heads, rows, onRow, selected, testid, empty = "Nothing recorded yet." }:
  { heads: string[]; rows: { key: string; cells: React.ReactNode[] }[]; onRow?: (k: string) => void; selected?: string; testid?: string; empty?: string }) {
  if (!rows.length) return <div className="emptyline" data-testid={`${testid}-empty`}>{empty}{empty === "Nothing recorded yet." && <EmptyNext testid={testid} />}</div>;
  return (
    <div className="table-wrap" role="region" aria-label="table" tabIndex={0} data-testid={testid}>
      <table>
        <thead><tr>{heads.map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={selected === r.key ? "selected" : ""} onClick={onRow ? () => onRow(r.key) : undefined}
                onKeyDown={onRow ? (e) => { if (e.key === "Enter") onRow(r.key); } : undefined} tabIndex={onRow ? 0 : undefined}
                style={onRow ? { cursor: "pointer" } : undefined} data-testid={`row-${r.key}`}>
              {r.cells.map((c, i) => <td key={i} data-label={heads[i]}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const Cell = ({ main, sub }: { main: React.ReactNode; sub?: React.ReactNode }) => (<><b>{main}</b>{sub ? <small>{sub}</small> : null}</>);

export function StateView({ error, retry }: { error: ApiError | null; retry: () => void }) {
  if (!error) return <div className="emptyline" data-testid="state-loading"><Loader2 className="spin" size={16} /> Loading current revisions…</div>;
  const Icon = error.status === 0 ? WifiOff : error.status === 403 || error.status === 404 ? Lock : CircleAlert;
  const msg = error.status === 404 ? "This record does not exist or you are not permitted to see it." : error.message;
  return (
    <div className="emptyline" data-testid={`state-error-${error.status}`}>
      <Icon size={18} /><p style={{ margin: "8px 0" }}>{msg}</p>
      {error.status !== 404 && error.status !== 403 && <button onClick={retry} data-testid="state-retry-btn"><RefreshCw size={14} />Try again</button>}
    </div>
  );
}

const CACHED = new Set(["/explorer", "/playground", "/portfolio"]);
const cacheKey = (p: string) => `larus:cache:${p}`;
export const SCREEN_BY_PREFIX: Record<string, string> = { src: "sources", source: "sources", prop: "review", proposal: "review", obl: "cash", obligation: "cash", fund: "cash", funding: "cash", dec: "approval", decision_item: "approval", not: "notices", notice: "notices", req: "conditions", requirement: "conditions", hand: "handover", handover: "handover", pkt: "packet", packet: "packet", task: "remaining", scen: "routes", scenario: "routes", flex: "flex", base: "approval", baseline: "approval" };
/* One rule for turning a record key into a screen; the server's `screen` wins when it supplied one. */
export const routeForRecord = (pid: string, key?: string | null, screen?: string | null, fallback = "conditions") => {
  const pre = (key || "").split("-")[0].toLowerCase();
  const s = screen || SCREEN_BY_PREFIX[pre] || fallback;
  return `${bp(pid)}/${s}${key ? `?record=${key}` : ""}`;
};

/* Show the last copy of a page at once (this tab only), then refresh it from the server. */
const readCache = (p: string) => { try { return JSON.parse(sessionStorage.getItem(cacheKey(p)) || "null"); } catch { return null; } };
const writeCache = (p: string, d: any) => { try { sessionStorage.setItem(cacheKey(p), JSON.stringify(d)); } catch { /* storage full: skip */ } };
export const clearPageCache = () => { Object.keys(sessionStorage).filter((k) => k.startsWith("larus:cache:")).forEach((k) => sessionStorage.removeItem(k)); };

export function useLoad<T = any>(path: string | null) {
  const [data, setData] = useState<T | null>(() => (path ? readCache(path) : null));
  const [error, setError] = useState<ApiError | null>(null);
  const current = useRef(path);
  current.current = path;
  const load = useCallback(() => {
    if (!path) return;
    setError(null);
    api<T>(path).then((d) => { writeCache(path, d); if (current.current === path) setData(d); }).catch((e) => { if (current.current === path) { setError(e); if (e.status === 401 || e.status === 403 || e.status === 404) setData(null); } });
  }, [path]);
  useEffect(() => { setData(path ? readCache(path) : null); load(); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = () => load(); window.addEventListener("larus:changed", h); window.addEventListener("larus:jobdone", h); return () => { window.removeEventListener("larus:changed", h); window.removeEventListener("larus:jobdone", h); }; }, [load]);
  return { data, error, reload: load };
}

export async function act<T = any>(fn: () => Promise<T>, ok: string, after?: () => void): Promise<T | undefined> {
  try {
    const r = await fn();
    toast.success(ok);
    window.dispatchEvent(new CustomEvent("larus:changed"));
    after?.();
    return r;
  } catch (e: any) {
    toast.error(e.status === 409 ? `Conflict: ${e.message}` : e.message);
    after?.();
    return undefined;
  }
}

export const KV = ({ k, v, testid }: { k: string; v: React.ReactNode; testid?: string }) => (
  <div className="routemetric" data-testid={testid}><label>{k}</label><span style={{ textAlign: "right" }}>{v ?? "-"}</span></div>
);

export const money = (a?: string | null, c = "AUD") => (a == null ? <span className="pill amber">Unknown</span> : `${c === "AUD" ? "A$" : c + " "}${Number(a).toLocaleString("en-AU")}`);

export const Reasons = ({ r }: { r?: string[] }) => (r && r.length ? <small>{plain(r.join(" · "))}</small> : null);
